import store from '@/store';
import axios from 'axios';
import { Vue } from 'vue-property-decorator';
import {Module, VuexModule, Mutation, Action} from 'vuex-module-decorators';
import { Mutations, Actions, Endpoints, Hococo, ViewModes, Buttons } from '@/ts/store/Constants';
import ConversationData from '@/ts/ConversationData';
import User from './User';
import ErrorResponse from '@/ts/ErrorResponse';
import { Connection } from '@/configuration/Connection';
import router from '@/router';

import {
    ConversationResponseData,
    SocketConversationResponseData,
    MessageResponseData,
    ConversationFetchParameters,
} from '@/ts/HococoInterfaces';

@Module({name: 'Messages', namespaced: true})
    export default class Messages extends VuexModule {
    public conversations: ConversationData[] = [];
    public activeConversationIndex: number = 0;
    public conversationMap: { [index: number]: number } = {};
    public conversationsLoaded: boolean = false;
    public currentMessage: string = '';
    public maxMessageId: number = 0;
    public updateTimerId: number | null = null;
    public personSuggestionVisibility: boolean = false;
    public latestRequestTimestamp: number = 0;
    public searchInitialized: boolean = false;
    public waitingForResponse: boolean = false;
    public visibleConversationsStatus: string = 'open';
    public conversationSearchTerm: string = '';
    public convertTicketAllTypes: any[] = [];
    public convertTicketAllCategories: any[] = [];
    public convertTicketAllStates: any[] = [];
    public convertTicketAllAssignee: any[] = [];
    public convertTicketAllWatchers: any[] = [];
    public suggestedStuffUsers: any[] = [];
    public messageAdminAttachment: any = null;
    public messageAdminAttachmentPdf: any = null;
    private addConversationDialogVisible: boolean = false;

    @Mutation
    public RESET_MESSAGES_STORE_STATE(): void {
        this.conversations                = [];
        this.activeConversationIndex      = 0;
        this.conversationMap              = {};
        this.conversationsLoaded          = false;
        this.currentMessage               = '';
        this.maxMessageId                 = 0;
        this.updateTimerId                = null;
        this.personSuggestionVisibility   = false;
        this.latestRequestTimestamp       = 0;
        this.searchInitialized            = false;
        this.waitingForResponse           = false;
        this.visibleConversationsStatus   = 'open';
        this.conversationSearchTerm       = '';
        this.convertTicketAllTypes        = [];
        this.convertTicketAllCategories   = [];
        this.convertTicketAllStates       = [];
        this.convertTicketAllAssignee     = [];
        this.convertTicketAllWatchers     = [];
        this.suggestedStuffUsers          = [];
        this.messageAdminAttachment       = null;
        this.messageAdminAttachmentPdf    = null;
        this.addConversationDialogVisible = false;
    }

    get unreadMessageCount(): number {
        const messages = store.getters['Messages/allMessages'];

        if (!messages) {
            return 0;
        }

        let count = 0;

        for (const message of messages) {
            if (message.unread === true && message.bySelf === false) {
                count++;
            }
        }

        return count;
    }

    get getMessageAdminAttachment() {
        return this.messageAdminAttachment;
    }

    get getMessageAdminAttachmentPdf() {
        return this.messageAdminAttachmentPdf;
    }

    get getConversationSearchTerm() {
        return this.conversationSearchTerm;
    }

    get conversationList() {
        return this.conversations;
    }

    get getVisibleConversationsStatus() {
        return this.visibleConversationsStatus;
    }

    get activeConversation(): ConversationData {
        const activeConversation = this.conversations[this.activeConversationIndex];
        if (activeConversation != null) {
            activeConversation.participants = activeConversation.participants.filter((element, index) => {
                return activeConversation.participants.indexOf(element) === index;
            });
            return activeConversation;
        }
    }

    get getConversation() {
        return (conversationId: number) => {
            return this.conversations.find((conversation: any) => conversation.id === conversationId);
        };
    }

    get getCurrentMessage() {
        return this.currentMessage;
    }

    get personSuggestionsVisible() {
        return this.personSuggestionVisibility;
    }

    get currentParticipants() {
        if (this.conversations.length === 0) {
            return [];
        }

        const activeConversation = this.conversations[this.activeConversationIndex];

        if (!activeConversation) {
            return [];
        }

        const filteredParticipants = activeConversation.participants.filter((element, index) => {
            return activeConversation.participants.indexOf(element) === index;
        });

        return filteredParticipants;
    }

    get isAuthorOfActiveConversation() {
        if (!this.conversations[this.activeConversationIndex]) {
            return false;
        }

        if (store.getters['User/getUserId'] === this.conversations[this.activeConversationIndex].authorId) {
            return true;
        }

        return false;
    }

    get allMessages() {
        const allMessages = [];

        for (const conversation of this.conversations) {
            for (const message of conversation.messages) {
                if (!message.isAction) {
                    allMessages.push(message);
                }
            }
        }

        return allMessages;
    }

    get isWaitingForResponse() {
        return this.waitingForResponse;
    }

    get getConvertTicketAllTypes() {
        return this.convertTicketAllTypes;
    }

    get getConvertTicketAllCategories() {
        return this.convertTicketAllCategories;
    }

    get getConvertTicketAllStates() {
        return this.convertTicketAllStates;
    }

    get getConvertTicketAllAssignee() {
        return this.convertTicketAllAssignee;
    }

    get getConvertTicketAllWatchers() {
        return this.convertTicketAllWatchers;
    }

    get getSuggestedStuffUsers() {
        return this.suggestedStuffUsers;
    }

    @Action
    public INITIALIZE_PROPERTY_CONVERSATION(propertyId: number) {
        axios.post(Endpoints.Admin.Conversations.CHECK_PROPERTY_CONVERSATION(propertyId))
            .then((response) => {
                if (!response.data.existing) {
                    const conversationData = response.data.conversation;

                    store.commit(Mutations.ADD_CONVERSATION, {
                        conversationId: conversationData.id,
                        participants: conversationData.participants.map((participan: any) => participan.id),
                        authorId: conversationData.author_id,
                        propertyId : conversationData.property_id,
                        status : conversationData.status,
                        title : conversationData.title,
                        external: conversationData.external,
                    });
                } else {
                    store.commit(Mutations.UPDATE_ACTIVE_CONVERSATION, response.data.conversation.id);
                }

                if (
                    !User.state.personData.isTenant &&
                    store.getters['Viewport/mobileLayout'] === true &&
                    router.currentRoute.name === 'units'
                ) {
                    router.push({path: `/user/inbox/${response.data.conversation.id}`});
                }

            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });

        return Promise.resolve();
    }

    @Action
    public INITIALIZE_DIRECT_CONVERSATION(userId: number) {
        axios.post(Endpoints.Admin.Conversations.ADD_DIRECT_CONVERSATION(userId))
            .then((response) => {
                const conversationData = response.data.conversation;

                store.commit(Mutations.ADD_CONVERSATION, {
                    conversationId: conversationData.id,
                    participants: conversationData.participants.map((participan: any) => participan.id),
                    authorId: conversationData.author_id,
                    propertyId : conversationData.property_id,
                    status : conversationData.status,
                    title : conversationData.title,
                    external: conversationData.external,
                });
                const name = User.state.personData.isTenant && store.getters['Viewport/mobileLayout'] === true ? 'messages' : 'admin-messages';
                router.push({
                    name,
                    params: {
                        conversationId: response.data.conversation.id.toString(),
                    },
                });
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public CHANGE_CONVERSATION_STATUS(data: any) {
        axios.post(Endpoints.Admin.Conversations.CHANGE_CONVERSATION_STATUS(data.status, data.conversationId))
            .then(() => {
                store.commit(Mutations.SET_CONVERSATION_STATUS, data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public CHANGE_CONVERSATION_TITLE(data: any) {
        axios.post(Endpoints.Admin.Conversations.CHANGE_CONVERSATION_TITLE(data.id), data)
            .then(() => {
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public TOGGLE_PERSON_FOR_CONVERSATION(data: any) {
        axios.post(
            Endpoints.Admin.Conversations.TOGGLE_PERSON_FOR_CONVERSATION(data.conversationId),
            { action: data.action, participantId: data.participantId },
            )
            .then(() => {
                if (data.action === 'add') {
                    store.commit(Mutations.REMOVE_FROM_SUGGESTED_STUFF_USERS, data.participantId);
                }
                store.commit(Mutations.TOGGLE_FOR_CONVERSATION, data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public CONVERT_CONVERSATION_TICKET(data: any) {
        axios.post(Endpoints.Admin.ConversationConvert.CONVERT_CONVERSATION_TICKET(data.conversationId), data.requestData)
            .then((response) => {
                store.commit(Mutations.REMOVE_CONVERSATION, data.conversationId);
                if (data.type === 'admin') {
                    store.commit(Mutations.SET_TICKET_CREATE_FORM_VISIBILITY, false);
                    if (store.getters['Socket/isSocketActive'] && store.getters['Socket/isTicketSocketInitialized']) {
                        store.dispatch(Actions.INITIALIZE_TICKET_SOCKET, {vue: data.vue, ticketId: response.data.id});
                        store.commit(Mutations.ADD_SOCKET_INIT_TICKET_ID, response.data.id);
                    }
                } else {
                    if (store.getters['Socket/isSocketActive'] && store.getters['Socket/isTicketSocketInitialized']) {
                        store.dispatch(Actions.INITIALIZE_USER_TICKET_SOCKET, {vue: data.vue, ticketId: response.data.id});
                        store.commit(Mutations.ADD_SOCKET_INIT_USER_TICKET_ID, response.data.id);
                    }
                    router.push({name: 'inbox'});
                }
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }


    @Action
    public FETCH_CONVERT_TICKET_ALL_TYPES() {
        axios.get(Endpoints.Admin.ConversationConvert.CONVERT_TICKET_ALL_TYPES)
            .then((response) => {
                store.commit(Mutations.SET_CONVERT_TICKET_ALL_TYPES, response.data.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_CONVERT_TICKET_ALL_CATEGORIES(data: any) {
        axios.get(Endpoints.Admin.ConversationConvert.CONVERT_TICKET_ALL_CATEGORIES(data.typeId, data.propertyId ))
            .then((response) => {
                store.commit(Mutations.SET_CONVERT_TICKET_ALL_CATEGORIES, response.data.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_CONVERT_TICKET_ALL_STATES() {
        axios.get(Endpoints.Admin.ConversationConvert.CONVERT_TICKET_ALL_STATES)
            .then((response) => {
                store.commit(Mutations.SET_CONVERT_TICKET_ALL_STATES, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_CONVERT_TICKET_ALL_ASSIGNEE(categoryId: number) {
        axios.get(Endpoints.Admin.ConversationConvert.CONVERT_TICKET_ALL_ASSIGNEE(categoryId))
            .then((response) => {
                store.commit(Mutations.SET_CONVERT_TICKET_ALL_ASSIGNEE, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_CONVERT_TICKET_ALL_WATCHERS() {
        axios.get(Endpoints.Admin.ConversationConvert.CONVERT_TICKET_ALL_WATCHERS)
            .then((response) => {
                store.commit(Mutations.SET_CONVERT_TICKET_ALL_WATCHERS, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_SUGGESTED_STUFF_USERS(data: any) {
        axios.get(Endpoints.Admin.Conversations.SUGGESTED_STUFF_USERS(data.conversationId, data.search))
            .then((response) => {
                const users = response.data;
                const usersObject = users && users.length && users.map((user) => {
                    return {
                        userId: user.person.user_id,
                        firstName: user.person.first_name,
                        familyName: user.person.family_name,
                        profileImageThumbnail: null,
                        roleId: user.corporation_roles ? user.corporation_roles[0].id : null,
                    };     
                });
                store.commit(Mutations.ADD_USERS_TO_CACHE, usersObject);
                store.commit(Mutations.SET_SUGGESTED_STUFF_USERS, users);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public INITIALIZE_MESSAGE_SEARCH() {
        store.search.registerResource('messages', {
            index: ['message'],
            getter: (state: any) => this.getters.allMessages,
            watch: { delay: 5000 },
        });

        Messages.state.searchInitialized = true;
    }

    @Action
    public START_CONVERSATION_UPDATE_TIMER() {
        Messages.state.updateTimerId = window.setTimeout(() => {
            store.dispatch(Actions.FETCH_CONVERSATIONS, {vue: null, forceUpdate: false});
        }, Hococo.CONVERSATION_UPDATE_INTERVAL);
    }

    @Action
    public INITIALIZE_CONVERSATION_SOCKET(vue: Vue) {
        if (!Connection.socketEnabled) {
            return;
        }

        if (!vue) {
            return;
        }

        const conversationChannel = 'user.' + store.getters['User/getUserId'] + '.conversations';

        vue.$echo.private(conversationChannel)
            .listen(('.conversation.new'), (conversationPayload: SocketConversationResponseData) => {
                for (const participant of conversationPayload.participants) {
                    if (!store.getters['User/getCacheUser'](participant.userId)) {
                        store.commit(Mutations.ADD_USER_TO_CACHE, participant);
                    }
                }

                store.commit(Mutations.ADD_CONVERSATION, conversationPayload);
                const socketData = {
                    conversationId : conversationPayload.conversationId,
                    vue,
                };
                store.dispatch(Actions.INITIALIZE_MESSAGE_CHANNEL_SOCKET, socketData);
            });

        store.commit(Mutations.CONVERSATION_SOCKET_INITIALIZED);
    }

    @Action
    public INITIALIZE_MESSAGE_CHANNEL_SOCKET(socketData: any) {
        if (!Connection.socketEnabled) {
            return;
        }

        socketData.vue.$echo.private('conversation.' + socketData.conversationId)
            .listen(('.conversation.new-message'), (messagePayload: any) => {
                store.commit(Mutations.ADD_CONVERSATION_MESSAGE, {
                    message_id: messagePayload.messageId,
                    conversation_id: messagePayload.conversationId,
                    author_id: messagePayload.authorId,
                    created_at: typeof messagePayload.createdAt.date === 'undefined' ? messagePayload.createdAt : messagePayload.createdAt.date,
                    content: messagePayload.content,
                    seen: messagePayload.seen,
                    storage_guid: messagePayload.storageGuid,
                    original_filename: messagePayload.originalFilename,
                    image_height: messagePayload.imageHeight,
                    image_width: messagePayload.imageWidth,
                    actionType: messagePayload.actionType,
                    actionTarget: (messagePayload.actionTarget !== null) ? messagePayload.actionTarget.id : null,
                });

                if (store.state.Identities.selectedIdentity.userType === 'tenant') {
                    store.commit(Mutations.UPDATE_IDENTITY_NOTIFICATIONS, { corporationId: messagePayload.corporationId });
                }
            });
    }

    @Action
    public FETCH_CONVERSATIONS(parameters: ConversationFetchParameters) {
        if (!Messages.state.searchInitialized) {
            store.dispatch(Actions.INITIALIZE_MESSAGE_SEARCH);
        }

        // If the socket is active, stop asking the server for new conversations and messages
        if (
            parameters.forceUpdate !== true &&
            store.getters['Socket/isSocketActive'] &&
            store.getters['Socket/isConversationSocketInitialized']
        ) {
            return;
        }

        if (Messages.state.conversationsLoaded && !Messages.state.maxMessageId) {
            return;
        }

        const endpoint = (Messages.state.conversationsLoaded)
            ? Endpoints.Conversations.CONVERSATION_UPDATES(Messages.state.maxMessageId)
            : Endpoints.Conversations.CONVERSATION_LIST;

        const timestamp = (new Date()).valueOf();

        axios.get(endpoint)
            .then((response) => {
                if (timestamp < Messages.state.latestRequestTimestamp) {
                    store.dispatch(Actions.START_CONVERSATION_UPDATE_TIMER);
                    return;
                }

                Messages.state.latestRequestTimestamp = timestamp;

                if (response.data.participants != null && response.data.participants.length > 0) {
                    for (const user of response.data.participants) {
                        if (!store.state.User.userCacheMap.hasOwnProperty(user.id)) {
                            store.commit(Mutations.ADD_USER_TO_CACHE, {
                                userId: user.id,
                                firstName: user.first_name,
                                familyName: user.family_name,
                                roleId: user.role_id,
                            });
                        }
                    }
                }

                store.commit(Mutations.REMOVE_NON_EXISTING_CONVERSATIONS, response.data.conversations);

                if (response.data.conversations != null && response.data.conversations.length > 0) {
                    for (const conversationResponse of response.data.conversations) {
                        if (!Messages.state.conversationMap.hasOwnProperty(conversationResponse.id)) {
                            store.commit(Mutations.ADD_CONVERSATION, {
                                conversationId: conversationResponse.id,
                                authorId: conversationResponse.author_id,
                                participants: conversationResponse.participants,
                                propertyId: conversationResponse.property_id,
                                status: conversationResponse.status,
                                title: conversationResponse.title,
                                external: conversationResponse.external,
                            });

                            if (parameters.vue) {
                                store.dispatch(
                                    Actions.INITIALIZE_MESSAGE_CHANNEL_SOCKET,
                                    {
                                        vue: parameters.vue,
                                        conversationId: conversationResponse.id,
                                    },
                                );
                            }
                        } else {
                            store.commit(Mutations.FRESH_CONVERSATION, {conversation: conversationResponse, conversationId: conversationResponse.id});
                        }
                    }
                }

                if (response.data.messages != null && response.data.messages.length > 0) {
                    store.commit(Mutations.ADD_CONVERSATION_MESSAGES, response.data.messages);
                }

                if (!Messages.state.conversationsLoaded) {
                    store.commit(Mutations.MARK_CONVERSATIONS_AS_LOADED);

                    store.dispatch(Actions.INITIALIZE_CONVERSATION_SOCKET, parameters.vue);

                    const activeConversation = Messages.state.conversations[Messages.state.activeConversationIndex];
                    const isDesktop = store.getters['Viewport/desktopLayout'];

                    if (isDesktop && activeConversation && activeConversation.hasUnseenMessages === true) {
                        store.dispatch(Actions.MARK_ACTIVE_CONVERSATION_AS_SEEN);
                    }
                }

                store.dispatch(Actions.START_CONVERSATION_UPDATE_TIMER);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });

        return Promise.resolve();
    }

    @Action
    public MARK_ACTIVE_CONVERSATION_AS_SEEN() {
        const conversation = Messages.state.conversations[Messages.state.activeConversationIndex];

        if (conversation.id > 0 && conversation.lastMessageId > 0) {
            const url = Endpoints.Conversations.MARK_CONVERSATION_AS_SEEN(
                conversation.id,
                conversation.lastMessageId,
            );
    
            axios.put(url)
                .then(() => {
                    store.commit(Mutations.MARK_CONVERSATION_AS_SEEN, conversation.id);
                })
                .catch((error) => {
                    if (error.response) {
                        const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                        store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                    } else {
                        console.log(error);
                    }
                });
        }

    }

    @Action
    public FETCH_CONVERSATION_PDF_PREVIEW(mediaId: number) {
        axios.get(Endpoints.Admin.Conversations.CONVERSATION_PDF_PREVIEW(mediaId))
            .then((response) => {
                console.log(response.data);
                const byteCharacters = atob(response.data);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const file = new Blob([byteArray], { type: 'application/pdf;base64' });
                const fileURL = URL.createObjectURL(file);
                window.open(fileURL);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public SEND_MESSAGE() {
        store.commit(Mutations.CREATE_MESSAGE, Messages.state.currentMessage);
    }

    @Mutation
    public UPDATE_CONVERSATION_SEARCH_TERM(searchTerm: string) {
        this.conversationSearchTerm = searchTerm;
        store.search.search('messages', searchTerm);
    }

    @Mutation
    public CREATE_MESSAGE() {
        let requestData: any;

        const conversationIndex = this.conversations[this.activeConversationIndex].id;
        const adminAttachmentPdf   = store.getters['Messages/getMessageAdminAttachmentPdf'];

        const adminAttachment   = store.getters['Messages/getMessageAdminAttachment'];

        const imageData = (store.getters['Camera/hasNewImageData'])
            ? store.getters['Camera/capturedImageData']
            : false;

        const imageMetaData = (imageData) ? store.getters['Camera/capturedImageMetaData'] : false;

        if (adminAttachmentPdf) {
            requestData = new FormData();
            requestData.append('attachmentPdf', adminAttachmentPdf);
            requestData.append('content', this.currentMessage);
            requestData.append('imageData', false);
            requestData.append('imageMetaData', false);
        } else {
            requestData = {
                content: this.currentMessage,
                imageData: false,
                imageMetaData: false,
                attachmentPdf: false,
            };

            if (imageData) {
                requestData.imageData = imageData;
                requestData.imageMetaData = imageMetaData;
            } else if (adminAttachment !== null) {
                requestData.imageData = adminAttachment.data;
                requestData.imageMetaData = adminAttachment.meta;
            }
        }

        Messages.state.waitingForResponse = true;

        axios.post(Endpoints.Conversations.MESSAGE(conversationIndex), requestData)
            .then((response) => {
                this.maxMessageId = response.data.id;

                if (imageData) {
                    store.commit(Mutations.ADD_ORIGINAL_IMAGE, {
                        guid: response.data.storage_guid,
                        originalImage: imageData,
                        width: response.data.image_width,
                        height: response.data.image_height,
                        ratio: response.data.image_ratio,
                    });
                }

                const newMessage: any = {
                    message_id : response.data.id,
                    author_id : response.data.author_id,
                    created_at : response.data.created_at,
                    content : response.data.body.content,
                    seen : false,
                    conversation_id :  response.data.conversation_id,
                    storage_guid : (response.data.attachment !== null) ? response.data.attachment.storage_guid : null,
                    original_filename : (response.data.attachment !== null) ? response.data.attachment.original_filename : null,
                    image_width : (response.data.attachment !== null) ? response.data.attachment.image_width : null,
                    image_height : (response.data.attachment !== null) ? response.data.attachment.image_height : null,
                    actionType : null,
                    actionTarget : null,
                };

                store.commit(Mutations.ADD_CONVERSATION_MESSAGE, newMessage);
                store.commit(Mutations.RESET_CAPTURED_IMAGE_DATA);

                Messages.state.waitingForResponse = false;
            })
            .catch((error) => {
                console.log(error);
                Messages.state.waitingForResponse = false;
            });


        this.currentMessage = '';
        return;
    }

    @Mutation
    public UPDATE_CURRENT_MESSAGE(message: string) {
        this.currentMessage = message;
    }

    // Sets the currently active conversation.
    // If no argument is given, it selects the most recently inserted conversation.
    @Mutation
    public UPDATE_ACTIVE_CONVERSATION(conversationId: number | null) {
        this.currentMessage = '';
        store.commit(Mutations.RESET_CAPTURED_IMAGE_DATA);
        this.activeConversationIndex = this.conversationMap[conversationId];
    }

    @Mutation
    public ADD_CONVERSATION_MESSAGE(message: MessageResponseData) {
        const arrayIndex = this.conversationMap[message.conversation_id];
        if (typeof this.conversations[arrayIndex] === 'undefined') {
            return;
        }

        if (message.message_id > this.maxMessageId) {
            this.maxMessageId = message.message_id;
        }

        if (message.message_id && !this.conversations[arrayIndex].hasMessageId(message.message_id)) {
            const byself = (store.getters['User/getUserId'] === message.author_id);
            this.conversations[arrayIndex].addMessage(
                message.message_id,
                message.author_id,
                byself,
                message.creation_date ? message.creation_date : message.created_at,
                message.content,
                byself ? false : (message.seen === null || message.seen === false),
                message.storage_guid,
                message.original_filename,
                message.image_width,
                message.image_height,
                message.actionType,
                message.actionTarget,
            );
        }
    }

    @Mutation
    public ADD_CONVERSATION_MESSAGES(messages: MessageResponseData[]) {
        for (const message of messages) {
            store.commit(Mutations.ADD_CONVERSATION_MESSAGE, message);
        }
    }

    @Mutation
    public ADD_CONVERSATION(conversationData: ConversationResponseData) {
        // Escape duplicate rows if socket is running
        if (this.conversationMap.hasOwnProperty(conversationData.conversationId)) {
            store.commit(Mutations.FRESH_CONVERSATION, {conversation: conversationData, conversationId: conversationData.conversationId});
        } else {
            let arrayIndex = null;

            arrayIndex = this.conversations.push(new ConversationData(
                conversationData.conversationId,
                conversationData.participants,
                [],
                conversationData.authorId,
                conversationData.propertyId,
                conversationData.status,
                conversationData.title,
                conversationData.external,
            )) - 1;

            this.conversationMap[conversationData.conversationId] = arrayIndex;
            store.commit(Mutations.UPDATE_ACTIVE_CONVERSATION, conversationData.conversationId);
        }
    }

    @Mutation
    public SET_MESSAGES_VIEW_MODE(viewMode: string): void {
        store.commit(Mutations.SET_VIEW_MODE, viewMode);

        switch (viewMode) {
            case ViewModes.Messages.Messages:
                store.commit(Mutations.SET_HEADER_TEXT, this.conversations[this.activeConversationIndex].shortParticipantNames(25));
                store.commit(Mutations.SET_LEFT_HEADER_BUTTON, Buttons.BackButton);
                store.commit(Mutations.SET_RIGHT_HEADER_BUTTON, Buttons.AbuseButton);
                store.commit(Mutations.SET_BOTTOM_MENU_VISIBILITY, false);
                break;

            case ViewModes.Messages.Conversations:
                store.commit(Mutations.SET_HEADER_TEXT, Vue.i18n.translate('header.messages', 'Messages'));
                store.commit(Mutations.SET_LEFT_HEADER_BUTTON, Buttons.None);
                store.commit(Mutations.SET_RIGHT_HEADER_BUTTON, Buttons.None);
                store.commit(Mutations.SET_BOTTOM_MENU_VISIBILITY, true);
                break;

            case ViewModes.Messages.AddPerson:
                store.commit(Mutations.SET_HEADER_TEXT, Vue.i18n.translate('header.add-participant', 'Add participant'));
                store.commit(Mutations.SET_LEFT_HEADER_BUTTON, Buttons.BackButton);
                store.commit(Mutations.SET_RIGHT_HEADER_BUTTON, Buttons.None);
                store.commit(Mutations.SET_BOTTOM_MENU_VISIBILITY, false);
                break;

            case ViewModes.Messages.Participants:
                store.commit(Mutations.SET_HEADER_TEXT, this.conversations[this.activeConversationIndex].shortParticipantNames(25));
                store.commit(Mutations.SET_LEFT_HEADER_BUTTON, Buttons.BackButton);
                store.commit(Mutations.SET_RIGHT_HEADER_BUTTON, Buttons.None);
                store.commit(Mutations.SET_BOTTOM_MENU_VISIBILITY, false);
                break;
        }
    }

    @Mutation
    public SET_ADD_CONVERSATION_DIALOG_VISIBILITY(visible: boolean): void {
        this.addConversationDialogVisible = visible;
    }

    @Mutation
    public MARK_CONVERSATION_AS_SEEN(conversationIndex: number) {
        const conversationArrayIndex = this.conversationMap[conversationIndex];
        this.conversations[conversationArrayIndex].markAsSeen();
    }

    @Mutation
    public MARK_CONVERSATIONS_AS_LOADED() {
        this.conversationsLoaded = true;
    }

    @Mutation
    public SET_VISIBLE_CONVERSATIONS_STATUS(status: string) {
        this.visibleConversationsStatus = status;
    }

    @Mutation
    public SET_ACTIVE_OPEN_CONVERSATIONS() {
        const openConversations = this.conversations.filter((conversation: any) => {
            return (conversation.status === 'open' && !conversation.external);
        });
        const activeConversationId = openConversations.length ? openConversations[openConversations.length - 1].id : null;

        store.commit(Mutations.UPDATE_ACTIVE_CONVERSATION, activeConversationId);
        if (activeConversationId) {
            store.dispatch(Actions.MARK_ACTIVE_CONVERSATION_AS_SEEN);
        }
    }

    @Mutation
    public SET_ACTIVE_CLOSE_CONVERSATIONS() {
        const closeConversations = this.conversations.filter((conversation: any) => {
            return (conversation.status === 'closed' && !conversation.external);
        });
        const activeConversationId = closeConversations.length ? closeConversations[closeConversations.length - 1].id : null;

        store.commit(Mutations.UPDATE_ACTIVE_CONVERSATION, activeConversationId);
        if (activeConversationId) {
            store.dispatch(Actions.MARK_ACTIVE_CONVERSATION_AS_SEEN);
        }
    }

    @Mutation
    public SET_ACTIVE_EXTERNAL_CONVERSATIONS() {
        const externalConversations = this.conversations.filter((conversation: any) => {
            return (conversation.status === 'open' && conversation.external);
        });
        const activeConversationId = externalConversations.length ? externalConversations[externalConversations.length - 1].id : null;

        store.commit(Mutations.UPDATE_ACTIVE_CONVERSATION, activeConversationId);
        if (activeConversationId) {
            store.dispatch(Actions.MARK_ACTIVE_CONVERSATION_AS_SEEN);
        }
    }

    @Mutation
    public SET_CONVERSATION_STATUS(data: any) {
        const conversation = this.conversations.find((cn: any) => cn.id === data.conversationId);
        conversation.status = data.status;
        if (data.type === 'admin') {
            const status = store.getters['Messages/getVisibleConversationsStatus'];
            status === 'open' ? store.commit(Mutations.SET_ACTIVE_OPEN_CONVERSATIONS) : store.commit(Mutations.SET_ACTIVE_CLOSE_CONVERSATIONS);
        } else {
            router.push({name: 'inbox'});
        }
    }

    @Mutation
    public REMOVE_CONVERSATION(conversationId: number) {
        this.conversations = this.conversations.filter((conversation: any) => {
            return conversation.id !== conversationId;
        });

        this.conversationMap = {};
        for (const [i, event] of this.conversations.entries()) {
            this.conversationMap[event.id] = i;
        }

        const status = store.getters['Messages/getVisibleConversationsStatus'];
        status === 'open' ? store.commit(Mutations.SET_ACTIVE_OPEN_CONVERSATIONS) : store.commit(Mutations.SET_ACTIVE_CLOSE_CONVERSATIONS);
    }

    @Mutation
    public REMOVE_NON_EXISTING_CONVERSATIONS(resConversations: any) {
        const resConversationsIds = resConversations.map((cn: any) => cn.id);
        this.conversations.forEach((conversation) => {
            if (!resConversationsIds.includes(conversation.id)) {
                store.commit(Mutations.REMOVE_CONVERSATION, conversation.id);
            }
        });
    }

    @Mutation
    public REMOVE_FROM_SUGGESTED_STUFF_USERS(participantId: number) {
        this.suggestedStuffUsers =  this.suggestedStuffUsers.filter((user: any) => {
            return participantId !== user.id;
        });
    }

    @Mutation
    public TOGGLE_FOR_CONVERSATION(data: any) {
        const conversation = store.getters['Messages/getConversation'](data.conversationId);
        if (data.action === 'remove') {
            conversation.participants = conversation.participants.filter((participant: any) => {
                return participant !== data.participantId;
            });
        } else {
            const participant = conversation.participants.find((pt: any) => pt.id === data.participantId);
            if (typeof participant  === 'undefined') {
                conversation.participants.push(data.participantId);
            }
        }
    }

    @Mutation
    public FRESH_CONVERSATION(data: any) {
        const conversation = this.conversations.find((cn: any) => cn.id === data.conversationId);
        conversation.participants = data.conversation.participants;
        conversation.status = data.conversation.status;
        const userId = store.getters['User/getUserId'];
        const canReadAllMessages = store.getters['User/getUser'].hasAccessTo('corporation-messages-management');

        if (!canReadAllMessages) {
            if (!data.conversation.participants.includes(userId)) {
                store.commit(Mutations.REMOVE_CONVERSATION, data.conversationId);
            }
        }
    }

    @Mutation
    public SET_SUGGESTED_STUFF_USERS(data: any) {
        this.suggestedStuffUsers = data;
    }

    @Mutation
    public SET_CONVERT_TICKET_ALL_CATEGORIES(data: any) {
        this.convertTicketAllCategories = data;
    }

    @Mutation
    public SET_CONVERT_TICKET_ALL_TYPES(data: any) {
        this.convertTicketAllTypes = data;
    }

    @Mutation
    public SET_CONVERT_TICKET_ALL_STATES(data: any) {
        this.convertTicketAllStates = data;
    }

    @Mutation
    public SET_CONVERT_TICKET_ALL_ASSIGNEE(data: any) {
        this.convertTicketAllAssignee = data;
    }

    @Mutation
    public SET_CONVERT_TICKET_ALL_WATCHERS(data: any) {
        this.convertTicketAllWatchers = data;
    }

    @Mutation
    public SET_MESSAGE_ADMIN_ATTACHMENT(file: any) {
        this.messageAdminAttachment = file;
    }

    @Mutation
    public SET_MESSAGE_ADMIN_ATTACHMENT_PDF(pdf: any) {
        this.messageAdminAttachmentPdf = pdf;
    }

    // not in use
    @Mutation
    public SHOW_PERSON_SUGGESTIONS(): void {
        // this.personSuggestionVisibility = true;
    }
}
